/**
 * @file utils.ts
 *
 * @fileoverview This file contains utility functions for generating breadcrumb
 * items for the job search page.
 */

import { Breadcrumb } from '~/jobiqo/components/Breadcrumbs';
import {
  createSelectedFacetBreadcrumb,
  getBreadcrumbPath,
  INDUSTRY_FACET,
  OCCUPATIONAL_FIELD_FACET,
  SelectedFacetBreadcrumb,
} from '~/jobiqo/components/Breadcrumbs/utils';
import { Facet } from '~/jobiqo/models/search';
import { getFacetAncestryChain } from '~/jobiqo/components/Search/facets/utils/facetHelpers';
// We only provide breadcrumbs for the industry and occupational field facets,
// as they are the most relevant for job search navigation. Other filters will not have
// breadcrumbs.
const JOB_SEARCH_BREADCRUMB_FACETS = [INDUSTRY_FACET, OCCUPATIONAL_FIELD_FACET];

interface BreadcrumbJob {
  title?: string;
  url?: {
    path?: string;
  };
}

export interface JobSearchBreadcrumbItemsParams {
  /**
   * The URL of the jobs page, which is used to generate the first breadcrumb item.
   */
  jobsPage: string;
  /**
   * The label for the "Jobs" breadcrumb item, which is displayed as the first item in the breadcrumb trail.
   */
  jobsLabel: string;
  /**
   * The facets used to filter the job search results, which are used to generate additional breadcrumb items.
   */
  facets?: Facet[] | null;
  /**
   * The query string parameters used to filter the job search results, which are used to generate additional breadcrumb items.
   */
  querystring?: Record<string, string | string[] | undefined>;
  /**
   * The current job being viewed, which is used to generate the last breadcrumb item. If a job is selected, its title and URL path will be used to create the final breadcrumb item.
   */
  currentJob?: BreadcrumbJob | null;
}

/**
 * Gets an array of query values from a string or array of strings.
 *
 * @param value
 *   The query string value(s) to process.
 * @returns
 *   An array of query values.
 */
function getQueryValues(value: string | string[] | undefined): string[] {
  if (!value) {
    // If the value is undefined or null, return an empty array.
    return [];
  }

  if (Array.isArray(value)) {
    // If the value is an array, convert each item to a string and return the array.
    return value.map((item) => item.toString());
  }

  // If the value is a string, split it by commas and filter out any empty strings.
  return value.split(',').filter(Boolean);
}

/**
 * Gets the selected facet breadcrumb data for a given facet name.
 *
 * @param facets
 *   The available facets from the job search results.
 * @param querystring
 *   The current query string parameters.
 * @param facetName
 *   The name of the facet to look up.
 * @returns
 *   The selected facet breadcrumb data, or null if none is selected.
 */
function getSelectedFacetBreadcrumbs(
  facets: Facet[] | null | undefined,
  querystring: Record<string, string | string[] | undefined> | undefined,
  facetName: string,
): SelectedFacetBreadcrumb[] {
  const selectedFilters = getQueryValues(querystring?.[facetName]);

  if (selectedFilters.length === 0) {
    return [];
  }

  const facet = facets?.find((item) => item.name === facetName);

  if (!facet?.values?.length) {
    return [];
  }

  const selectedFilter = selectedFilters[0];
  const selectedFacetChain = getFacetAncestryChain(
    selectedFilter,
    facet.values,
  );

  if (!selectedFacetChain.length) {
    return [];
  }

  const normalizedChain =
    facetName === OCCUPATIONAL_FIELD_FACET && selectedFacetChain.length > 1
      ? selectedFacetChain.slice(-2)
      : selectedFacetChain.slice(-1);

  return normalizedChain
    .filter((selectedFacetValue) => selectedFacetValue.label)
    .map((selectedFacetValue) =>
      createSelectedFacetBreadcrumb({
        name: facetName,
        label: selectedFacetValue.label,
        filter: selectedFacetValue.filter.toString(),
        alias: selectedFacetValue.alias,
      }),
    );
}

/**
 * Gets the breadcrumb items for the job search page.
 *
 * @param params
 *   The parameters for generating the breadcrumb items.
 * @returns
 *   An array of breadcrumb items starting with the jobs root.
 */
export function getJobSearchBreadcrumbItems({
  jobsPage,
  jobsLabel,
  facets,
  querystring,
  currentJob,
}: JobSearchBreadcrumbItemsParams): Breadcrumb[] {
  const items: Breadcrumb[] = [
    {
      label: jobsLabel,
      path: jobsPage,
    },
  ];
  const selectedFacets: SelectedFacetBreadcrumb[] = [];

  JOB_SEARCH_BREADCRUMB_FACETS.forEach((facetName) => {
    const selectedFacetChain = getSelectedFacetBreadcrumbs(
      facets,
      querystring,
      facetName,
    );

    if (!selectedFacetChain.length) {
      // If no facet is selected for this facet name, we skip adding a breadcrumb item.
      return;
    }

    selectedFacetChain.forEach((selectedFacet) => {
      selectedFacets.push(selectedFacet);
      items.push({
        label: selectedFacet.label,
        path: getBreadcrumbPath(jobsPage, selectedFacets),
      });
    });
  });

  if (currentJob?.title && currentJob.url?.path) {
    items.push({
      label: currentJob.title,
      path: currentJob.url.path,
    });
  }

  return items;
}

/**
 * Checks whether the job search breadcrumb trail has items beyond the root.
 *
 * @param params
 *   The parameters for generating the breadcrumb items, without the jobs label.
 * @returns
 *   True if there is at least one breadcrumb item beyond the root jobs item.
 */
export function hasJobSearchBreadcrumbItems(
  params: Omit<JobSearchBreadcrumbItemsParams, 'jobsLabel'>,
): boolean {
  return (
    getJobSearchBreadcrumbItems({
      ...params,
      jobsLabel: '',
    }).length > 1
  );
}
